// ---- Core Document Types (CouchDB-ready) ----

export interface Organization {
  _id: string;
  _rev: string;
  type: 'organization';
  displayId?: string;
  parentOrgId?: string;
  name: string;
  abbreviation: string;
  description: string;
  website: string;
  logoUrl: string;
  active: boolean;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  programs?: Program[];
}

export interface Program {
  _id: string;
  _rev: string;
  type: 'program';
  displayId?: string;
  organizationId: string;
  name: string;
  abbreviation: string;
  description: string;
  active: boolean;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  releaseTrains?: ReleaseTrain[];
}

export interface ReleaseTrain {
  _id: string;
  _rev: string;
  type: 'releasetrain';
  displayId?: string;
  programId: string;
  organizationId: string;
  name: string;
  abbreviation: string;
  description: string;
  /** Legacy string form still loads; FE-1 introduced the structured object. */
  cadence: 'quarterly' | 'pi' | 'custom' | Cadence;
  active: boolean;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  projects?: Project[];
}

export interface Project {
  _id: string;
  _rev: string;
  type: 'project';
  displayId?: string;
  organizationId: string;
  programId: string;
  releaseTrainId: string;
  name: string;
  /** The WHAT — what this project is. */
  description: string;
  /** The WHY — why this product or project needs to exist at all. */
  problemDetails?: string;
  /** The HOW — technical architecture, tools, and how it fits the estate. */
  technicalDescription?: string;
  status: ProjectStatus;
  priority: Priority;
  health: HealthStatus;
  startDate: string;
  endDate: string;
  owner: string;
  members: string[];
  tags: string[];
  budget: Budget;
  customFields: Record<string, unknown>;
  /** owner/name slugs whose commits and PRs belong to this project (F5). */
  repositories?: string[];
  /** Optional status moves on PR events; empty means off. */
  scmAutoTransition?: Record<string, string>;
  /** Workflow binding (F6/F7): per work-item type, then a project-wide default. */
  workflowBindings?: Record<string, string>;
  defaultWorkflowId?: string;
  createdAt: string;
  updatedAt: string;
  stats?: ProjectStats;
}

/** A move filed for approval: addressed to a permission, decided by `required` holders. */
export interface PendingApproval {
  requestId: string;
  status: string;
  to: string;
  permission: string;
  approvals: number;
  required: number;
  requestedBy: string;
  requestedAt: number;
  expiresAt?: number;
}

export interface WorkItemCheckout {
  holder: { kind: 'user' | 'token'; id: string; email?: string; label?: string; actsFor?: string | null };
  since: string;
  until: string;
  reason?: string;
  batch?: string;
}

export interface WorkItem {
  _id: string;
  _rev: string;
  type: 'workitem';
  /**
   * A LOCAL badge, not an identifier. Features, stories and tasks are numbered
   * per PARENT, so `ST-3` means "the third story under its feature" — 82% of
   * the estate shares one with a sibling in the same project. Render `key`.
   */
  displayId?: string;
  /**
   * The canonical, estate-unique reference, e.g. `PO46-828`. Immutable once
   * allocated. This is what a person should quote, search for and paste into a
   * comment. Optional only for items created before the backfill; use
   * `itemRef()` rather than reading it directly.
   */
  key?: string;
  workItemType: WorkItemType;
  projectId: string;
  parentId: string | null;
  title: string;
  description: string;
  status: string;
  priority: Priority;
  assignee: string;
  startDate: string;
  dueDate: string;
  estimatedHours: number;
  loggedHours: number;
  storyPoints: number;
  businessValue: number;
  timeCriticality: number;
  riskReductionOpportunity: number;
  jobSize: number;
  wsjfScore: number;
  order: number;
  dependencies: string[];
  /** Set while a move of this item awaits approval (EP-26 FE-7); cleared when it closes. */
  pendingApproval?: PendingApproval | null;
  /** Who is working it, until when (EP-28). Expired = free. */
  checkout?: WorkItemCheckout | null;
  /** Derived, only when a list is asked for ?withDependencies=1. */
  openBlockers?: number;
  /** How many items wait on THIS one — the half that says "do it first". */
  blocks?: number;
  blockedBy?: Array<{ _id: string; key: string; title: string; status: string }>;
  /** Distinct SCORED items (epic/feature/story) delayed by this one. */
  delaysScoredItems?: number;
  /** Cost of delay released by clearing this — the sum of what waits behind it. */
  unblockedCoD?: number;
  /**
   * (ownCoD + unblockedCoD) / jobSize. Not a bonus: WSJF ranks by cost of
   * delay, and delaying a blocker delays everything behind it. Derived per
   * request — `wsjfScore` remains the stored, human-supplied number.
   */
  effectiveWsjf?: number;
  tags: string[];
  githubUser?: string;
  createdByTokenId?: string;
  sprintId?: string;
  releaseId?: string;
  acceptanceCriteria?: Array<{ text: string; checked: boolean }>;
  doneWhen?: Array<{ text: string; checked: boolean }>;
  customFields: Record<string, unknown>;
  // CRM opportunity (workItemType === 'opportunity')
  oppAccount?: string;
  oppClientId?: string;
  /**
   * The `orgId` of the RESELLER that sells and services this deal — half of
   * the research platform's write-once `(client_id, partner_id)` binding.
   * Origination refuses to enqueue without it, and it can never be corrected
   * afterwards, only abandoned.
   */
  oppPartnerOrgId?: string;
  /** Which of OUR companies sells this deal — a `parent` account's `orgId`
   *  (Heuristic Systems or West42). Not the partner, who sells it through. */
  oppSellerOrgId?: string;
  /** Set once the research engagement exists. Its presence means the
   *  `(client_id, partner_id)` binding is fixed on the far side. */
  researchEngagementId?: string;
  researchSyncStatus?: string;
  researchSyncError?: string;
  /** Delivered, but short — currently only: assessments with no entitlementId. */
  researchSyncWarning?: string;
  researchSkipReason?: string;
  oppContactName?: string;
  oppContactEmail?: string;
  oppSource?: string;
  /** Minor units. Never a float — see the backend note. */
  oppValueMinor?: number;
  oppCurrency?: string;
  oppRegion?: string;
  oppProbability?: number;
  oppExpectedCloseDate?: string;
  sourceLeadId?: string;
  oppLostReason?: string;
  // CRM lead (workItemType === 'lead')
  leadName?: string;
  leadEmail?: string;
  leadOrganisation?: string;
  leadPhone?: string;
  leadSource?: string;
  leadPage?: string;
  leadReferrer?: string;
  leadUtm?: Record<string, string>;
  leadSubmissions?: { receivedAt: string; message: string; subject: string; source: string; page: string }[];
  leadSubmissionCount?: number;
  leadDisqualifiedReason?: string;
  convertedToId?: string;
  // Ideation (workItemType === 'idea')
  ideaLevel?: IdeaLevel | '';
  ideaStatus?: IdeaStatus;
  targetPiId?: string;
  targetOrgId?: string;
  targetProgramId?: string;
  proposedBy?: string;
  realizedItemId?: string;
  realizedPiId?: string;
  createdAt: string;
  updatedAt: string;
}

export type Weekday =
  | 'monday' | 'tuesday' | 'wednesday' | 'thursday'
  | 'friday' | 'saturday' | 'sunday' | '';

export type ReleaseOffset =
  | 'sameDayAsSprintEnd' | 'onOrAfterSprintEnd' | 'nextWeekAfterSprintEnd';

/** Structured cadence (FE-1). Held as defaults on a release train, then copied
 *  onto a Program Increment at creation where it becomes authoritative. */
export interface Cadence {
  sprintLengthDays: number;      // 1 = daily, 7 = weekly, 7n = n-weekly
  sprintStartWeekday: Weekday;
  sprintEndWeekday: Weekday;
  releaseWeekday: Weekday;
  releaseOffset: ReleaseOffset;
  workingDays: string[];
  blackoutDates: string[];
  timezone: string;
  autoGenerateReleases: boolean;
  ipSprint: boolean;
  autoAdvance?: boolean;          // the dispatcher switches sprints at sprintBoundaryTime
  sprintBoundaryTime?: string;    // HH:MM in `timezone`; required when autoAdvance
}

export interface CadencePreviewSprint {
  sprintNumber: number;
  name: string;
  startDate: string;
  endDate: string;
  releaseDate: string;
  releaseShifted: boolean;
}

export interface CadencePreview {
  cadence: Cadence;
  startDate: string;
  endDate: string;
  sprintLengthWeeks: number;
  sprints: CadencePreviewSprint[];
  warnings: string[];
}

export interface ProgramIncrement {
  _id: string;
  _rev: string;
  type: 'programincrement';
  name: string;
  description: string;
  // Hierarchy + project association (FE-2)
  programId?: string;
  releaseTrainId?: string;
  projectIds?: string[];
  startDate: string;
  sprintCount: number;
  sprintLengthWeeks: number;
  sprintLengthDays?: number;
  cadence?: Cadence;
  endDate: string;
  status: 'planned' | 'active' | 'completed';
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

/** Write shape for a PI. The API merges a partial cadence over the release
 *  train's defaults, so callers need only send the fields they are setting —
 *  unlike a read, where `cadence` always comes back fully populated. */
export interface ProgramIncrementInput
  extends Omit<Partial<ProgramIncrement>, 'cadence'> {
  cadence?: Partial<Cadence>;
}

export interface Sprint {
  _id: string;
  _rev: string;
  type: 'sprint';
  piId: string;
  name: string;
  sprintNumber: number;
  startDate: string;
  endDate: string;
  status: 'planned' | 'active' | 'completed';
  goal: string;
  createdAt: string;
  updatedAt: string;
}

export interface Release {
  _id: string;
  _rev: string;
  type: 'release';
  version: string;
  name: string;
  description: string;
  sprintId: string;
  releaseDate: string;
  // F3 widened the lifecycle: draft is where generated releases start,
  // closed is terminal (see closureReason).
  status: 'draft' | 'planned' | 'released' | 'cancelled' | 'closed';
  notes: string;
  featureIds: string[];
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  /** Arrives with F3 (Release model v2). Optional until then. */
  projectId?: string;
  piId?: string;
  releaseTrainId?: string;
  workItemIds?: string[];
  releaseType?: 'scheduled' | 'standalone';
  plannedDate?: string;
  closureReason?: string;
  publicNotes?: string;
}

export interface Comment {
  _id: string;
  _rev: string;
  type: 'comment';
  workItemId: string;
  author: string;
  /** The token that wrote it for `author` (runner:<project>:<session>, an agent, a training token). */
  viaToken?: { id?: string; name?: string };
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface TimeEntry {
  _id: string;
  _rev: string;
  type: 'timeentry';
  workItemId: string;
  userId: string;
  hours: number;
  date: string;
  description: string;
  createdAt: string;
}

export interface Activity {
  _id: string;
  _rev: string;
  type: 'activity';
  action: string;
  targetType: string;
  targetId: string;
  userId: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface Attachment {
  _id: string;
  _rev: string;
  type: 'attachment';
  workItemId: string;
  fundingId?: string;
  filename: string;
  storedPath: string;
  size: number;
  mimeType: string;
  uploadedBy: string;
  documentType?: string;
  version?: string;
  approvalStatus?: 'draft' | 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

// ---- Enums / Union Types ----

export type ProjectStatus = 'planning' | 'active' | 'on-hold' | 'completed' | 'archived';
export type Priority = 'low' | 'medium' | 'high' | 'critical';
export type HealthStatus = 'on-track' | 'at-risk' | 'behind';
export type WorkItemType =
  | 'epic' | 'feature' | 'story' | 'task' | 'bug' | 'security'
  /** A design-standard finding on UI/UX work (hs-pmo/ep43#12); blocker/major ones hold the close. */
  | 'design'
  | 'idea' | 'lead' | 'opportunity'
  /** A dead-lettered delivery that needs a human (EP-14 FE-9). */
  | 'integration';

// ---- Ideation ----
// An idea proposes a new capability. On approval it spawns an epic/feature and
// is kept as a linked "realized" record.
export type IdeaLevel = 'epic' | 'feature' | 'project' | 'program';
export type IdeaStatus = 'draft' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'parked' | 'realized';

// `badge` holds full, static Tailwind class strings (dynamic `bg-${x}` would be
// purged by the JIT compiler). `dot` is the pipeline-column accent.
export const IDEA_STATUS_META: Record<IdeaStatus, { label: string; badge: string; dot: string }> = {
  draft:     { label: 'Draft',     badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',       dot: 'bg-gray-400' },
  submitted: { label: 'Submitted', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',    dot: 'bg-blue-500' },
  in_review: { label: 'In Review', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', dot: 'bg-amber-500' },
  approved:  { label: 'Approved',  badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',  dot: 'bg-green-500' },
  rejected:  { label: 'Rejected',  badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',         dot: 'bg-red-500' },
  parked:    { label: 'Parked',    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',    dot: 'bg-slate-400' },
  realized:  { label: 'Realized',  badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', dot: 'bg-violet-500' },
};

// ---- Funding Pipeline Types ----

export type FundingStage = 'prospect' | 'qualifying' | 'preparing' | 'submitted' | 'under-review' | 'awarded' | 'declined' | 'withdrawn';
export type FunderType = 'federal' | 'state' | 'local' | 'foundation' | 'corporate' | 'nonprofit' | 'international' | 'other';
export type FundingType = 'grant' | 'contract' | 'cooperative-agreement' | 'subgrant' | 'sponsorship' | 'donation' | 'loan' | 'venture' | 'other';

export interface FundingContact {
  name: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  notes: string;
}

export interface BudgetLineItem {
  id: string;
  category: string;
  description: string;
  amount: number;
  actual: number;
  notes: string;
}

export interface YearlyAllocation {
  year: number;
  amount: number;
  actual: number;
  status: 'planned' | 'approved' | 'disbursed' | 'closed';
  notes: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
  stage: string;
  completedAt: string;
  completedBy: string;
  dueDate: string;
}

export interface ComplianceRequirement {
  id: string;
  type: string;
  title: string;
  dueDate: string;
  status: 'pending' | 'in-progress' | 'submitted' | 'approved' | 'overdue';
  submittedDate: string;
  assignee: string;
  notes: string;
  linkedDeliverables: string[];
}

export interface FundingOpportunity {
  _id: string;
  _rev: string;
  type: 'funding';
  name: string;
  description: string;
  funderName: string;
  funderType: FunderType;
  funderWebsite: string;
  funderId: string;
  contacts: FundingContact[];
  fundingType: FundingType;
  amount: number;
  currency: string;
  stage: FundingStage;
  owner: string;
  teamMembers: string[];
  identifiedDate: string;
  deadlineDate: string;
  decisionDate: string;
  startDate: string;
  endDate: string;
  probability: number;
  priority: Priority;
  tags: string[];
  category: string;
  programArea: string;
  projectId: string;
  eligibilityNotes: string;
  matchRequirement: number;
  notes: string;
  budgetItems: BudgetLineItem[];
  indirectRate: number;
  indirectAmount: number;
  yearlyAllocations: YearlyAllocation[];
  checklist: ChecklistItem[];
  complianceRequirements: ComplianceRequirement[];
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface FundingPipelineSummary {
  total: number;
  totalValue: number;
  weightedValue: number;
  byStage: Record<string, { count: number; value: number }>;
  byFundingType: Record<string, number>;
  byFunder: Record<string, { count: number; value: number; awarded: number }>;
  byPriority: Record<string, number>;
  awarded: number;
  awardedValue: number;
  pending: number;
  pendingValue: number;
  declined: number;
  declinedValue: number;
  winRate: number;
  avgDealSize: number;
  upcomingDeadlines: Array<{ id: string; name: string; deadline: string; stage: string }>;
  complianceDue: Array<{ fundingId: string; fundingName: string; title: string; dueDate: string; status: string; type: string }>;
  budgetSummary: { totalBudgeted: number; totalActual: number };
}

// ---- Funder CRM Types ----

export type FunderRelationshipStatus = 'active' | 'prospect' | 'inactive' | 'former';

export interface FunderAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface FunderInteraction {
  date: string;
  type: string;
  subject: string;
  notes: string;
  participants: string[];
}

export interface Funder {
  _id: string;
  _rev: string;
  type: 'funder';
  name: string;
  abbreviation: string;
  description: string;
  funderType: FunderType;
  website: string;
  portalUrl: string;
  address: FunderAddress;
  contacts: FundingContact[];
  interactions: FunderInteraction[];
  relationshipStatus: FunderRelationshipStatus;
  preferredContactMethod: string;
  focusAreas: string[];
  fundingRange: { min: number; max: number };
  typicalDuration: string;
  applicationCycles: string[];
  tags: string[];
  notes: string;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  stats?: FunderStats;
}

export interface FunderStats {
  totalOpportunities: number;
  awarded: number;
  awardedValue: number;
  pending: number;
  pendingValue: number;
  winRate: number;
}

// ---- Config Types ----

export interface StatusConfig {
  id: string;
  label: string;
  color: string;
  order: number;
}

export interface PriorityConfig {
  id: string;
  label: string;
  color: string;
}

export interface WorkItemTypeConfig {
  id: string;
  label: string;
  color: string;
  icon: string;
}

export interface HealthConfig {
  id: string;
  label: string;
  color: string;
}

export interface DashboardConfig {
  widgets: string[];
  quickLinks: QuickLink[];
}

export interface QuickLink {
  label: string;
  url: string;
  icon?: string;
}

export interface AppConfig {
  statuses: StatusConfig[];
  priorities: PriorityConfig[];
  workItemTypes: WorkItemTypeConfig[];
  healthStatuses: HealthConfig[];
  dashboard: { widgets: string[]; quickLinks: string[] };
}

// ---- Stats ----

export interface ProjectStats {
  total: number;
  completed: number;
  inProgress: number;
  overdue: number;
  completion: number;
}

export interface Budget {
  allocated: number;
  spent: number;
  currency: string;
}

// ---- Resources / Assignees ----

export type ResourceRole =
  | 'project-manager'
  | 'developer'
  | 'designer'
  | 'analyst'
  | 'executive'
  | 'coordinator'
  | 'consultant'
  | 'admin'
  | 'other';

export type ResourceType = 'user' | 'token' | 'resource';

export interface Resource {
  _id: string;
  _rev: string;
  type: 'resource';
  name: string;
  email: string;
  phone: string;
  role: ResourceRole;
  resourceType: ResourceType;
  linkedId: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---- UI State ----

export type PanelType = 'projects' | 'tasks' | 'settings' | 'funding' | 'funders' | 'org-admin' | 'api-docs' | 'docs' | 'release-notes' | 'program-increments' | null;
export type ViewMode = 'kanban' | 'list' | 'gantt';

// ---- Dashboard Config ----

export interface DashboardTile {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  size: 'small' | 'medium' | 'large';
}

export interface DashboardConfig {
  _id: string;
  _rev: string;
  type: 'dashboardconfig';
  userId: string;
  organizationId: string;
  quickAccessProjectIds: string[];
  /** Automatic, most-recent-first and capped. Distinct from the curated list. */
  recentProjectIds?: string[];
  /** Default board scope when the URL carries none. */
  lastProjectId?: string;
  tiles: DashboardTile[];
  createdAt: string;
  updatedAt: string;
}

/** One clause of a contract template. */
export interface TemplateSection {
  id: string;
  heading?: string;
  body?: string;
  order?: number;
  /** Breaks out of the two-column portrait layout. True for the title block,
   *  parties clause, tables and the signature block — a signature block split
   *  across a column break on an agreement about to be signed is a defect. */
  spansColumns?: boolean;
  /** 1 = clause, 2 = subsection within it. */
  level?: number;
  /** The clause number as it appears in the agreement (`11.3`). */
  number?: string;
  /** Kept in the template but left out of the rendered document. Not a delete —
   *  a clause dropped for one negotiation is routinely reinstated. */
  excluded?: boolean;
}

/** A version of a contract template. */
export interface ContractTemplate {
  _id: string;
  _rev?: string;
  type?: 'contracttemplate';
  /** Stable across versions: `master-reseller` stays itself from 1.0 to 4.2. */
  templateId: string;
  /** Mirrors the backend `TEMPLATE_KINDS`. `productschedule` is a template kind
   *  only — a Schedule is appended to an order form, never generated as a
   *  contract of its own, which is why `ContractState.kind` does not carry it. */
  kind?: 'master' | 'orderform' | 'partnercommercial' | 'productschedule' | 'familyloan';
  title: string;
  description?: string;
  /** Empty until published. Set by the server, never by the client. */
  version?: string;
  status?: 'draft' | 'published' | 'retired';
  publishedAt?: string;
  publishedBy?: string;
  /** Present once a contract has been generated from it — this is what freezes
   *  the version. */
  firstUsedAt?: string;
  usageCount?: number;
  sections?: TemplateSection[];
  createdAt?: string;
  updatedAt?: string;
}

/** One contract as it appears in the by-organisation tree. */
export interface GenerationTargets {
  partners: { id: string; name: string; hasMaster: boolean }[];
  /** Empty when no version is published — the reason generation is unavailable. */
  masterTemplateVersion: string;
  masterTemplateId: string;
}

/** Contract state for one deal, as the deal views need it. */
export interface ContractState {
  id: string;
  key: string;
  status: string;
  kind: 'master' | 'orderform' | 'partnercommercial' | 'familyloan';
  executionDate: string;
  signatureMethod?: '' | 'manual' | 'esignature' | 'placeholder';
}

export interface ContractRow {
  contractKind?: 'master' | 'orderform' | 'partnercommercial' | 'familyloan';
  /** Slot -> filename. Empty when rendering or the share failed. */
  documents?: string[];
  /** Why there is no document, shown in place of the download. */
  documentError?: string;
  /** How it was signed: manual, esignature, or placeholder (nothing yet). */
  signatureMethod?: '' | 'manual' | 'esignature' | 'placeholder';
  id: string;
  key?: string;
  title: string;
  status: string;
  templateId?: string;
  /** The template version it was generated from — what proves which terms were
   *  signed, since a published version is frozen once used. */
  templateVersion?: string;
  executionDate?: string;
}

export interface ContractOrgNode {
  id: string;
  name: string;
  orgType?: string;
  environment?: string;
  contracts: ContractRow[];
  clients: ContractOrgNode[];
}

/** Partners hold the master agreement; each client's order form is executed
 *  under it, which is why the listing is a hierarchy rather than a flat list. */
export interface ContractTree {
  partners: ContractOrgNode[];
  /** Organisations with no partner — shown, not hidden: a client without one
   *  cannot have an order form, and that is worth seeing. */
  unassigned: ContractOrgNode[];
  unmatched: ContractRow[];
  /** Private agreements between individuals (family loans). Only a super_admin
   *  sees them, and only a super_admin may raise one. */
  private?: ContractRow[];
  mayRaisePrivate?: boolean;
}

/** What raising a family loan sends. Money in MINOR units, the rate in basis
 *  points: the server computes the schedule and never sees a float. */
export interface FamilyLoanTerms {
  lenderName: string;
  lenderAddress?: string;
  borrowerName: string;
  borrowerAddress?: string;
  relationship?: string;
  currency: string;
  principalMinor: number;
  rateBps: number;
  instalments: number;
  initialMonths: number;
  /** The day the money arrives. Usually unknown when the agreement is drawn
   *  up: the schedule is then counted in months and the date is recorded on
   *  the agreement at transfer. */
  advanceDate?: string;
  /** The currency the money is SENT in, when not the loan's own. */
  advanceCurrency?: string;
  /** Loan-currency units per 1 unit of `advanceCurrency` (EUR 1 = USD 1.1188
   *  is `"1.1188"`), fixed for the life of the loan. */
  setRate?: string;
  setRateDate?: string;
}
